import { LitElement, html } from 'lit';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { iframeResizer } from 'iframe-resizer';
import {openModalOnFitModalTargetClick, openModalOnNestedModalOpen} from './fit-modal-target';

import styles from './fit-modal.css';

const bodyScrollLockOptions = {
  reserveScrollBarGap: true,
};

const modalStack = [];

function pushToOpenStack(modal) {
  modalStack.forEach((modal) => modal.close());
  modalStack.push(modal);
}

function popFromOpenStack(modal) {
  if (modal === modalStack[modalStack.length - 1]) {
    modalStack.pop();
    const previous = modalStack.pop();
    previous && previous.open();
  }
}

class FitModal extends LitElement {
  static get is() { return 'fit-modal'; }

  static get styles() { return styles; }

  render() {
    return html`
      <div id="scroller" @click="${this.onClick}">
        <div id="content">
          <div id="modal-content">
            <div id="modal-header">
              ${this.noHeader
    ? html`
                <button id="dog-ear-close" dialog-dismiss></button>`
    : html`
                <h3>${this.title}</h3>
                <button id="modal-header-close" dialog-dismiss>×</button>`}
              <div id="progress-bar"></div>
            </div>
            <slot id="slot" @slotchange="${this.onSlotchange}"></slot>
          </div>
        </div>
      </div>
`;
  }

  static get properties() {
    return {
      opened: {
        type: Boolean,
        reflect: true,
      },

      title: {
        type: String,
      },

      noHeader: {
        type: Boolean,
        attribute: 'no-header',
        reflect: true,
      },

      allowOutsideScroll: {
        type: Boolean,
        attribute: 'allow-outside-scroll',
        reflect: true,
      },

      noBackdrop: {
        type: Boolean,
        attribute: 'no-backdrop',
        reflect: true,
      },

      loading: {
        type: Boolean,
        reflect: true,
      },
    };
  }

  open() {
    this.opened = true;
  }

  close() {
    this.opened = false;
  }

  constructor(props) {
    super(props);
    this.opened = false;
    this.loading = false;
    this.intersectionObserver = new IntersectionObserver((entries) => { entries[0] && this.onPosition(entries[0]); });
    this.iframeResizerMap = new Map();
  }

  disconnectedCallback() {
    clearAllBodyScrollLocks();
  }

  toggle() {
    this.opened = !this.opened;
  }

  firstUpdated() {
    this.$ = {
      scroller: this.shadowRoot.querySelector('#scroller'),
      content: this.shadowRoot.querySelector('#content'),
      modalContent: this.shadowRoot.querySelector('#modal-content'),
      slot: this.shadowRoot.querySelector('#slot'),
    };
  }

  updated(changedProperties) {
    if (changedProperties.has('opened')) {
      this.opened ? this.onOpen() : this.onClose();
      this.hidden = !this.opened;
    }
  }

  onOpen() {
    this.intersectionObserver.observe(this);
    if (this.allowOutsideScroll) {
      document.body.style.position = 'relative';
    } else {
      disableBodyScroll(this.$.scroller, bodyScrollLockOptions);
    }
    pushToOpenStack(this);
    setTimeout(() => {
      this.setAttribute('visible', '');
    });
  }

  onClose() {
    this.removeAttribute('visible');
    popFromOpenStack(this);
    if (this.allowOutsideScroll) {
      document.body.style.position = '';
    } else {
      enableBodyScroll(this.$.scroller);
    }
    this.intersectionObserver.unobserve(this);
  }

  onLoad(loading) {
    this.loading = loading;
  }

  onClick(event) {
    if (!event.composed) {
      return;
    }
    const composedPath = event.composedPath();
    if (!composedPath.includes(this.$.modalContent) || composedPath.some((node) => node instanceof HTMLElement && node.hasAttribute('dialog-dismiss'))) {
      this.close();
    }
  }

  onPosition(observerEntry) {
    const potentialTop = Math.max(observerEntry.intersectionRect.y, Math.abs(observerEntry.boundingClientRect.y));
    this.$.content.style['min-height'] = `${observerEntry.intersectionRect.height}px`;
    this.$.content.style.top = potentialTop ? `${potentialTop}px` : '';
  }

  onSlotchange() {
    const iframes = this.$.slot.assignedNodes().filter((node) => node instanceof HTMLIFrameElement);

    this.iframeResizerMap.forEach((iframeResizer, iframe) => {
      if (!iframes.includes(iframe)) {
        this.iframeResizerMap.delete(iframe);
      }
    });

    iframes.forEach((iframe) => {
      if (!this.iframeResizerMap.has(iframe)) {
        this.listenToIframeReadyState(iframe);
        this.iframeResizerMap.set(iframe, this.createIframeResizer(iframe));
      }
    });
  }

  listenToIframeReadyState(iframe, listener) {
    this.loading = true;
    iframe.contentWindow.addEventListener('beforeunload', () => { this.loading = true; });
    iframe.addEventListener('load', () => { // this is a guard, if the iframe src returns an error
      this.loading = false;
      try {
        this.title = iframe.contentWindow.document.title || this.title;
      } catch (e) {
        this.title = '';
      }
    });
  }

  createIframeResizer(iframe, listener) {
    let iframeOptions = {};

    try {
      iframeOptions = JSON.parse(iframe.getAttribute('fit-iframe')) || {};
    } catch (e) {
      (console.error || console.log).call(console, e.stack || e);
    }

    iframeOptions.initCallback = () => {
      this.loading = false;
    };

    iframeOptions.closedCallback = () => {
      this.close();
    };

    iframeOptions.messageCallback = ({iframe, message}) => {
      if (message.type === 'openModal') {
        openModalOnNestedModalOpen(message);
      }
    };

    return iframeResizer(iframeOptions, iframe);
  }
}

customElements.define(FitModal.is, FitModal);

document.addEventListener('click', openModalOnFitModalTargetClick);
